export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  const trimmed = timeZone.trim();
  if (!trimmed) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

export function resolveDisplayTimeZone(preference?: string | null): string | undefined {
  const trimmed = preference?.trim();
  if (!trimmed) return undefined;
  return isValidIanaTimeZone(trimmed) ? trimmed : undefined;
}

export function formatTimeZoneLabel(timeZone: string): string {
  return timeZone.replace(/_/g, ' ');
}

/** UTC offset label for a zone at the given instant (e.g. GMT+3, UTC). */
export function formatTimeZoneOffset(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

export function formatDisplayTimeZoneOptionLabel(timeZone: string): string {
  const offset = formatTimeZoneOffset(timeZone);
  return `${formatTimeZoneLabel(timeZone)} (${offset})`;
}

/** Fallback when Intl.supportedValuesOf is unavailable. */
const DISPLAY_TIMEZONE_FALLBACK: readonly string[] = [
  'UTC',
  'Europe/London',
  'Europe/Athens',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
];

/** @deprecated Use getDisplayTimeZoneOptions() for the full list. */
export const DISPLAY_TIMEZONE_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  DISPLAY_TIMEZONE_FALLBACK.map((value) => ({
    value,
    label: formatDisplayTimeZoneOptionLabel(value),
  }));

function getIntlSupportedTimeZones(): string[] | undefined {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  if (typeof intl.supportedValuesOf !== 'function') return undefined;
  return intl.supportedValuesOf('timeZone');
}

function getIanaTimeZones(): string[] {
  try {
    const zones = getIntlSupportedTimeZones();
    if (zones?.length) {
      return [...zones].sort((a, b) => a.localeCompare(b));
    }
  } catch {
    // fall through to static list
  }
  return [...DISPLAY_TIMEZONE_FALLBACK];
}

let cachedDisplayTimeZoneOptions: ReadonlyArray<{ value: string; label: string }> | null = null;

/** All IANA timezones for pickers (cached). */
export function getDisplayTimeZoneOptions(): ReadonlyArray<{ value: string; label: string }> {
  if (cachedDisplayTimeZoneOptions) return cachedDisplayTimeZoneOptions;
  cachedDisplayTimeZoneOptions = getIanaTimeZones().map((value) => ({
    value,
    label: formatDisplayTimeZoneOptionLabel(value),
  }));
  return cachedDisplayTimeZoneOptions;
}

/** Browser default + full zone list; ensures the saved value is always present. */
export function buildTimeZoneSelectOptions(
  currentValue?: string | null,
): Array<{ value: string; label: string }> {
  const browserDefault = getBrowserTimeZone();
  const options = [...getDisplayTimeZoneOptions()];
  const trimmed = currentValue?.trim();
  if (trimmed && isValidIanaTimeZone(trimmed) && !options.some((o) => o.value === trimmed)) {
    options.unshift({ value: trimmed, label: formatDisplayTimeZoneOptionLabel(trimmed) });
  }
  return [
    { value: '', label: `Browser default (${formatTimeZoneLabel(browserDefault)})` },
    ...options,
  ];
}

export function localDateKey(date: Date, timeZone?: string): string {
  return date.toLocaleDateString('en-CA', timeZone ? { timeZone } : undefined);
}
